<script setup lang="ts">
import { PV } from "@/widgets";
import type SignaturePad from "signature_pad";
import { nextTick, ref, useTemplateRef, watch, watchEffect } from "vue";
import UpdateIcon from "~icons/ic/baseline-edit";
import ResetIcon from "~icons/prime/times";
import { defineActions, defineState } from "../common";
import { FormGuide, FormHead, FormMessage } from "../internal";
import { type BaseProps, defaultProps } from "../props";

type ModelType = string;
interface PropsType extends BaseProps<ModelType> {
    placeholder?: string;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
});

const model = defineModel<ModelType | undefined>({
    default: undefined,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => model.value !== undefined,
});

const actions = defineActions(model, props, state);

const padShown = ref(false);
const padCanvas = useTemplateRef("pad");
const padObject = ref<SignaturePad>();

watchEffect(() => {
    if (model.value === "" || model.value === null) {
        model.value = undefined;
    }
});

watch(
    () => padShown.value,
    (showPad) => {
        if (showPad) setupPad();
    },
    { immediate: true },
);

async function setupPad() {
    const SignaturePad = (await import("signature_pad")).default;
    await nextTick();

    if (padCanvas.value === null) {
        return;
    }

    padObject.value = new SignaturePad(padCanvas.value, {
        minWidth: 1,
        maxWidth: 4,
        penColor: "rgb(0, 0, 0)",
    });
}

function onSubmit() {
    model.value = padObject.value?.toDataURL() ?? undefined;
    padShown.value = false;
}

function onDelete() {
    padObject.value?.clear();
}

function onShowPad() {
    if (props.disabled === true) return;
    padShown.value = true;
}
</script>

<template>
    <div class="flex flex-col gap-1">
        <FormHead :actions="actions" :param="props" :state="state" />

        <template v-if="!state.editMode">
            <div class="relative h-full hover:border-0">
                <div class="rns-sign-box-inactive" :class="{ 'border-0!': props.embedMode === 'inline' }">
                    <template v-if="state.hasValue">
                        <div class="h-full aspect-square">
                            <img class="h-full w-full" :src="model" />
                        </div>
                    </template>
                    <template v-else>
                        <span class="text-xl text-gray-800">
                            {{ props.placeholder ?? props.label }}
                        </span>
                    </template>
                </div>
                <div
                    v-if="state.updatable"
                    class="absolute top-0 left-0 right-0 bottom-0 hover:bg-black hover:cursor-pointer hover:opacity-60 opacity-0"
                >
                    <div class="flex flex-col h-full items-center justify-center">
                        <div v-tooltip.top="'Ubah'">
                            <UpdateIcon class="text-gray-400 text-[3rem]" @click="actions.onUpdate" />
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <div class="relative h-full">
                <div class="rns-sign-box" :class="{ 'border-0!': props.embedMode === 'inline' }" @click="onShowPad">
                    <template v-if="state.hasValue">
                        <div class="h-full aspect-square">
                            <img class="h-full w-full" :src="model" />
                        </div>
                    </template>
                    <template v-else>
                        <span class="text-xl text-gray-800">
                            {{ props.placeholder ?? props.label }}
                        </span>
                    </template>
                </div>
                <div
                    v-if="state.hasValue && state.clearable"
                    class="absolute top-0 left-0 right-0 bottom-0 z-10 hover:bg-black hover:cursor-pointer hover:opacity-60 opacity-0 rounded-md"
                >
                    <div class="flex flex-col h-full items-center justify-center">
                        <div v-tooltip.top="'Reset'">
                            <ResetIcon class="text-gray-400 text-[4rem]" @click="actions.onReset" />
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <FormGuide :state="state" :text="props.guideText" />
        <FormMessage :state="state" />

        <PV.Drawer v-model:visible="padShown" position="full">
            <template #header>
                <div class="flex flex-row items-center w-full">
                    <div class="font-bold text-2xl">{{ props.label }}</div>
                </div>
            </template>

            <template #footer>
                <div class="flex flex-row items-center justify-end gap-4 w-full">
                    <PV.Button label="Hapus" severity="danger" size="small" @click="onDelete" />
                    <PV.Button label="Simpan" size="small" @click="onSubmit" />
                </div>
            </template>

            <div class="flex flex-row items-center justify-center w-full h-full p-4">
                <canvas ref="pad" class="border border-gray-400 aspect-square" height="420" width="420"></canvas>
            </div>
        </PV.Drawer>
    </div>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-sign-box {
    @apply border border-gray-300 hover:border-gray-400 shadow-xs rounded-md;
    @apply h-50 w-full flex flex-row items-center justify-center hover:cursor-pointer;
}

.rns-sign-box-inactive {
    @apply rns-g-static-input;
    @apply h-50 w-full flex flex-row items-center justify-center;
}
</style>
